/**
 * ✅ RESTAURANT MUTFAK VE YAZDIR İŞLEMLERİ HOOK'U
 * 
 * Bu hook, restoran modunda mutfak ve yazdırma işlemlerini yönetir:
 * - handlePrintToKitchen: Mutfağa sipariş yazdır
 * - handlePrintCheck: Adisyon fişi yazdır
 * - handleBackButtonClick: Geri butonu kontrolü (yazdırılmamış ürün kontrolü)
 * - handleContinueWithoutPrinting: Yazdırmadan devam et
 * - handlePrintToKitchenAndGoBack: İlave ürünleri yazdır ve geri dön
 * - handlePrintCancellationAndGoBack: İptal fişi yazdır ve geri dön
 * - handlePrintBothAndGoBack: Hem ilave hem iptal yazdır
 * - handleCloseTable: Adisyon iptal (masayı kapat)
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback, useRef } from 'react';
import type { CartItem } from '../../quickSale/types';
import type { KitchenOrderItem, KitchenOrderSubItem, KitchenPrintResult } from '../components/KitchenPrintService';
import type { NavigateFunction } from 'react-router-dom';

// Hook parametreleri için tip tanımı
export interface UseRestaurantKitchenOperationsParams {
  // Temel bilgiler
  isRestaurantMode: boolean;
  useKitchenScreen: boolean;
  tableId: string | null;
  tableName: string | null;
  restaurantScreenId: number | null;
  companyId: string | null;
  waiterName: string | null;
  
  // Sepet
  cart: CartItem[];
  clearCart: () => void;
  calculateTotal: () => number;
  
  // SQL veriler
  sqlInventoryItems: any[];
  allCategoriesData: any[];
  
  // Ekran ayarları
  screenSettings: any;
  currentScreen: any;
  quickSaleUserData: any;
  
  // Sipariş numaraları
  reservedLongCode: string | null;
  reservedShortCode: string | null;
  
  // Navigasyon
  navigate: NavigateFunction;
  
  // Snackbar
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  
  // State setters
  setIsClosingTable: React.Dispatch<React.SetStateAction<boolean>>;
  setUnprintedItemsList: React.Dispatch<React.SetStateAction<any[]>>;
  setCancelledItemsList: React.Dispatch<React.SetStateAction<any[]>>;
  setUnprintedItemsWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsReceiptPrintedForCurrentTable: React.Dispatch<React.SetStateAction<boolean>>;
  showKitchenPrintInfoModal?: (
    printedItems: { name: string; quantity: number }[],
    unprintedItems: { name: string; quantity: number }[],
    /** ✅ YENİ: Kategorisinde yazıcı atanmadığı için yazdırılamayan ürünler */
    skippedItems?: { name: string; quantity: number; categoryName?: string }[],
  ) => void;
  /**
   * ✅ YENİ: Kategori bazlı yazdırmada, sepetteki TÜM ürünlerin kategorisine
   * yazıcı atanmamış olduğunda gösterilecek uyarı modalı.
   * Örn: tek bir ürün var, kategorisine yazıcı atanmamış → bu çağrılır.
   */
  showNoCategoryPrinterModal?: (
    skippedCategoryNames: string[],
    skippedItems: { name: string; quantity: number; categoryName?: string }[],
  ) => void;

  // State değerleri
  cancelledItemsList: any[];
  unprintedItemsList: any[];
  
  // Diğer fonksiyonlar
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;
  printKitchenReceiptByCategory: (data: any) => Promise<KitchenPrintResult>;
  
  // İndirim bilgileri
  lastAppliedDiscountAmount?: number;
  appliedDiscountType?: 'percentage' | 'amount' | null;
  discountInputPercentage?: string;

  // Servisler
  salesService: any;
  
  // Sepet güncelleme (printedQuantity için)
  setCart?: React.Dispatch<React.SetStateAction<CartItem[]>>;

  /** Mutfağa yazdır (yeni kalemler) başarıyla tamamlandığında — örn. masalara dönüş geri sayımı */
  onAfterSuccessfulKitchenPrint?: () => void;

  /**
   * Adisyon (müşteri fişi) başarıyla yazdırıldığında tetiklenir — masalara
   * dönüş geri sayımı için. `onAfterSuccessfulKitchenPrint` ile aynı geri
   * dönüş davranışını sağlar; QuickSaleScreen aynı `scheduleRestaurantReturnAfterKitchenPrint`
   * fonksiyonunu buraya da geçer.
   */
  onAfterSuccessfulCheckPrint?: () => void;

  /** Seçili cari hesap adı (fişte gösterilmek üzere) */
  selectedCustomerName?: string | null;

  /**
   * ✅ YENİ: SADECE adisyon fişinde kullanılacak garson adı (override).
   * Restoran ekran ayarları "Adisyonda İlk Açan Garson" toggle'ı açıkken,
   * QuickSaleScreen tarafından masayı ilk açan garsonun adı buraya verilir.
   * Verilmezse fallback olarak `waiterName` kullanılır.
   * Mutfak yazdırma, iptal vb. diğer akışlar bu değerden ETKİLENMEZ.
   */
  receiptWaiterName?: string | null;
}

// Hook dönüş tipi
export interface UseRestaurantKitchenOperationsReturn {
  /**
   * Ana mutfağa yazdır fonksiyonu.
   * `options.skipCountdown=true` verilirse başarı sonrası geri sayım modalı
   * (`onAfterSuccessfulKitchenPrint`) tetiklenmez. Geri-dönüş modal
   * akışlarından çağrılırken kullanılır (zaten direkt navigate yapılır).
   */
  handlePrintToKitchen: (options?: { skipCountdown?: boolean }) => Promise<void>;
  handlePrintCheck: () => Promise<void>;
  handleBackButtonClick: () => Promise<void>;
  handleContinueWithoutPrinting: () => void;
  handlePrintToKitchenAndGoBack: () => Promise<void>;
  handlePrintCancellationAndGoBack: () => Promise<void>;
  handlePrintBothAndGoBack: () => Promise<void>;
  handleCloseTable: (cancellationReason?: string) => Promise<void>;
  /**
   * ✅ YENİ: Yazdırılmış bir ürünün silinmesi/azaltılması anında, ilgili
   * miktar için iptal fişi oluşturup mutfak yazıcılarına gönderir.
   * `handlePrintToKitchen`'ın aksine SQL state'iyle ilgilenmez (yani cart vs
   * existingItems karşılaştırması yapmaz, `markAsPrinted`'i çağırmaz, ilave
   * sipariş yazdırması yapmaz). Sadece verilen item ve `cancelledQuantity`
   * parametreleriyle iptal fişi oluşturur.
   *
   * Hata durumunda silme/azaltma akışını bloke etmez; sadece warning loglar.
   */
  printCancellationForRemovedItem: (params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => Promise<void>;
}

/**
 * Restaurant modu için mutfak ve yazdırma işlemleri hook'u
 */
export const useRestaurantKitchenOperations = ({
  isRestaurantMode,
  useKitchenScreen,
  tableId,
  tableName,
  restaurantScreenId,
  companyId,
  waiterName,
  cart,
  clearCart,
  calculateTotal,
  sqlInventoryItems,
  allCategoriesData,
  screenSettings,
  currentScreen,
  quickSaleUserData,
  reservedLongCode,
  reservedShortCode,
  navigate,
  showSnackbar,
  setIsClosingTable,
  setUnprintedItemsList,
  setCancelledItemsList,
  setUnprintedItemsWarningOpen,
  setIsReceiptPrintedForCurrentTable,
  showKitchenPrintInfoModal,
  showNoCategoryPrinterModal,
  cancelledItemsList,
  unprintedItemsList,
  addWaiterAction,
  printKitchenReceiptByCategory,
  lastAppliedDiscountAmount,
  appliedDiscountType,
  discountInputPercentage,
  salesService,
  setCart,
  onAfterSuccessfulKitchenPrint,
  onAfterSuccessfulCheckPrint,
  selectedCustomerName,
  receiptWaiterName,
}: UseRestaurantKitchenOperationsParams): UseRestaurantKitchenOperationsReturn => {

  // 🛡️ Eşzamanlılık kilidi: handlePrintToKitchen çalışırken (SQL okuma →
  // mutfak API → yazıcı → markAsPrinted) ikinci bir tetikleme mükerrer fiş
  // üretir. Buton disabled olmadığı için çift tıklama / sabırsız tekrar basma
  // bu ref ile engellenir.
  const kitchenPrintInFlightRef = useRef(false);

  // ─────────────────────────────────────────────────────────
  // Kategori → yazıcı çözümleyici (ekran ayarı; istemcide kalır).
  // Kategori bazlı modda yazıcı atanmamış kategorideki ürün basılmaz ama
  // sunucuda "yazdırıldı" sayılır (kasten yazıcı atanmamış kategori mantığı).
  // ─────────────────────────────────────────────────────────
  const buildPrinterResolver = () => {
    const parsed = (() => {
      const raw = (screenSettings as any)?.PrintingSettingsJson;
      if (!raw) return null;
      try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
    })();
    const isCategoryMode = parsed?.printingMode === 'category';
    const assignments: Record<string, string | undefined> = parsed?.categoryPrinterAssignments || {};
    const assignments2: Record<string, string | undefined> = parsed?.categoryPrinterAssignments2 || {};

    // stockVisibility kategorisi ÖNCELİKLİ — yazıcı atamaları bu kategorilere yapılır
    const productCategoryLookup: Record<string, string> = {};
    const allProductSettings = screenSettings?.stockVisibility?.products || {};
    for (const wId of Object.keys(allProductSettings)) {
      const wp = allProductSettings[wId];
      if (wp && typeof wp === 'object') {
        for (const cId of Object.keys(wp)) {
          const pIds = wp[cId];
          if (Array.isArray(pIds)) {
            for (const pid of pIds) {
              if (!productCategoryLookup[pid]) productCategoryLookup[pid] = cId;
            }
          }
        }
      }
    }

    const categoryIdOf = (productId: string, fallback?: string): string | undefined => {
      const inv = sqlInventoryItems.find((x: any) => x.id === productId);
      return productCategoryLookup[productId] || fallback || inv?.categoryId || undefined;
    };
    const categoryNameOf = (categoryId?: string): string | undefined =>
      categoryId ? allCategoriesData.find((c: any) => c.id === categoryId)?.name : undefined;
    const hasPrinter = (productId: string, fallback?: string): boolean => {
      if (!isCategoryMode) return true;
      const catId = categoryIdOf(productId, fallback);
      if (!catId) return false;
      return !!assignments[catId] || !!assignments2[catId];
    };

    return { isCategoryMode, categoryIdOf, categoryNameOf, hasPrinter };
  };

  /** JSON dizi alanını güvenle oku (başka cihazın eklediği satırlarda menü/seçenek bilgisi JSON'dan gelir). */
  const parseJsonArray = (raw?: string | null): any[] => {
    if (!raw) return [];
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  };

  /**
   * Sunucudan dönen fiş satırlarını mutfak fişi verisine çevirir.
   * Menü/seçenek alt kalemleri: satır bu cihazın sepetindeyse sepetten,
   * değilse satırın JSON alanlarından okunur.
   */
  const buildKitchenReceipt = (
    ticket: { PrintNo: number; TableName: string; IsAdditionalOrder: boolean; ShortCode?: string | null; PrintedBy?: string | null },
    lines: { ItemId: number; ProductId: string; Name: string; Quantity: number; Note?: string | null; SelectedOptionsJson?: string | null; MenuNormalItemsJson?: string | null }[],
  ) => {
    const { categoryIdOf, categoryNameOf } = buildPrinterResolver();
    const pick = (o: any, ...keys: string[]) => {
      for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null) return o[k];
      return undefined;
    };

    const items: KitchenOrderItem[] = lines.map((line) => {
      const qty = line.Quantity;
      const cartRow = cart.find((c: CartItem) => c.sqlItemId === line.ItemId) as any;
      const normals: any[] = cartRow?.menuNormalItems?.length ? cartRow.menuNormalItems : parseJsonArray(line.MenuNormalItemsJson);
      const options: any[] = cartRow?.selectedOptions?.length ? cartRow.selectedOptions : parseJsonArray(line.SelectedOptionsJson);

      const subItems: KitchenOrderSubItem[] = [];
      for (const n of normals) {
        subItems.push({
          id: String(pick(n, 'itemId', 'ItemId') ?? ''),
          name: String(pick(n, 'itemName', 'ItemName') ?? ''),
          quantity: Number(pick(n, 'quantity', 'Quantity') ?? 1) * qty,
          isExtraOption: false,
        });
      }
      for (const o of options) {
        const extra = pick(o, 'selectedExtraOption', 'SelectedExtraOption');
        if (extra) {
          const diff = Number(pick(extra, 'priceDifference', 'PriceDifference') ?? 0);
          subItems.push({
            id: String(pick(extra, 'optionItemId', 'OptionItemId') ?? ''),
            name: `${pick(extra, 'optionItemName', 'OptionItemName') ?? ''} (+${diff.toFixed(2)} TL)`,
            quantity: Number(pick(o, 'quantity', 'Quantity') ?? 1) * qty,
            isExtraOption: true,
          });
        } else {
          subItems.push({
            id: String(pick(o, 'itemId', 'ItemId') ?? ''),
            name: String(pick(o, 'itemName', 'ItemName') ?? ''),
            quantity: Number(pick(o, 'quantity', 'Quantity') ?? 1) * qty,
            isExtraOption: false,
          });
        }
      }

      const categoryId = categoryIdOf(line.ProductId, cartRow?.categoryId);
      return {
        id: line.ProductId,
        name: line.Name,
        quantity: qty,
        note: line.Note || cartRow?.note || undefined,
        categoryName: categoryNameOf(categoryId),
        categoryId,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    });

    return {
      orderNumber: ticket.ShortCode || reservedShortCode || `RS-${Date.now()}`,
      tableName: ticket.TableName || tableName || undefined,
      waiterName: ticket.PrintedBy || waiterName || undefined,
      items,
      orderNote: undefined,
      companyName: screenSettings?.receiptDisplaySettings?.companyName || undefined,
      isAdditionalOrder: ticket.IsAdditionalOrder,
    };
  };

  /** Yeni istek anahtarı (sunucu aynı anahtarı ikinci kez görürse fişi tekrar basmaz). */
  const newRequestId = (): string =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

  /**
   * Restaurant Modu - Adisyon İptal Fonksiyonu
   * "Adisyon İptal" butonuna basıldığında çağrılır
   */
  const handleCloseTable = useCallback(async (cancellationReason?: string) => {
    if (!isRestaurantMode || !restaurantScreenId || !tableId || !companyId) {
      console.error('[ADİSYON İPTAL] ❌ Gerekli bilgiler eksik:', { isRestaurantMode, restaurantScreenId, tableId, companyId });
      showSnackbar('Masa bilgisi bulunamadı.', 'error');
      return;
    }

    try {
      console.log('[ADİSYON İPTAL] 🍽️ Adisyon iptal ediliyor...', {
        restaurantScreenId,
        tableId,
        tableName,
        companyId,
        cartItems: cart.length
      });

      setIsClosingTable(true);

      const { restaurantActiveSaleApi, restaurantCompletedSaleApi, restaurantTableApi } = await import('../../../../services/restaurantApiService');

      // 1️⃣ Aktif satışı al
      console.log('[ADİSYON İPTAL] 📡 getByTableId çağrılıyor, tableId:', parseInt(tableId));
      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      console.log('[ADİSYON İPTAL] 📡 getByTableId yanıtı:', activeSale ? { Id: activeSale.Id, BranchId: activeSale.BranchId, TotalAmount: activeSale.TotalAmount, TableId: activeSale.TableId } : 'NULL');
      
      if (!activeSale) {
        showSnackbar('Bu masa için aktif satış bulunamadı.', 'warning');
        setIsClosingTable(false);
        return;
      }

      // 2️⃣ Masa bilgilerini al
      const table = await restaurantTableApi.getById(parseInt(tableId));

      // 3️⃣ Son waiter action'ı kaydet
      await addWaiterAction(activeSale.Id, 'cancel_check', {
        cartItems: cart.length,
        totalAmount: activeSale.TotalAmount,
        reason: 'Adisyon iptal edildi'
      });

      const updatedActiveSale = await restaurantActiveSaleApi.getById(activeSale.Id);

      // 4️⃣ RestaurantCompletedSales'e taşı - UNPAID olarak
      const createPayload = {
        CompanyId: companyId,
        BranchId: activeSale.BranchId || null,
        RestaurantScreenId: restaurantScreenId,
        TableId: parseInt(tableId),
        TableName: table.Name,
        TableGroupName: table.TableGroupName || null,
        SaleNumber: `IPTAL-${tableId}-${Date.now()}`,
        SubTotal: activeSale.SubTotal,
        DiscountAmount: activeSale.DiscountAmount,
        TaxAmount: activeSale.TaxAmount,
        TotalAmount: activeSale.TotalAmount,
        ItemsJson: JSON.stringify(activeSale.Items),
        PaymentMethod: 'UNPAID',
        PaymentDetailsJson: null,
        OpenedAt: activeSale.OpenedAt,
        OpenedBy: activeSale.OpenedBy,
        OpenedByName: waiterName || 'Bilinmeyen',
        CompletedBy: waiterName || 'Bilinmeyen',
        CompletedByName: waiterName || 'Bilinmeyen',
        Notes: `${activeSale.Notes || ''} [ADİSYON İPTAL EDİLDİ]${cancellationReason ? ` [İPTAL NEDENİ: ${cancellationReason}]` : ''}`.trim(),
        WaiterActionsJson: updatedActiveSale.WaiterActionsJson,
        CancellationReason: cancellationReason || null,
      };
      console.log('[ADİSYON İPTAL] 📤 restaurantCompletedSaleApi.create çağrılıyor...', createPayload);
      
      const createResult = await restaurantCompletedSaleApi.create(createPayload);
      console.log('[ADİSYON İPTAL] 📥 create yanıtı:', JSON.stringify(createResult));

      console.log('[ADİSYON İPTAL] ✅ RestaurantCompletedSales\'e UNPAID olarak taşındı!');

      // 5️⃣ RestaurantActiveSales'den sil
      await restaurantActiveSaleApi.delete(activeSale.Id);

      console.log('[ADİSYON İPTAL] ✅ RestaurantActiveSales\'den silindi!');
      
      showSnackbar(`${tableName} adisyonu iptal edildi!`, 'warning');
      
      clearCart();
      
      setTimeout(() => {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
      }, 500);

    } catch (error: any) {
      console.error('[ADİSYON İPTAL] ❌ Hata:', error);
      console.error('[ADİSYON İPTAL] ❌ Hata detayı:', error?.response?.data || error?.message);
      showSnackbar(`Hata oluştu: ${(error as Error).message}`, 'error');
    } finally {
      setIsClosingTable(false);
    }
  }, [isRestaurantMode, restaurantScreenId, tableId, tableName, companyId, cart, waiterName, showSnackbar, setIsClosingTable, addWaiterAction, clearCart, navigate]);

  /**
   * ✅ YENİ: Yazdırılmış ürünün silinmesi/azaltılması anında otomatik tetiklenen
   * iptal fişi yazdırma fonksiyonu.
   *
   * `handlePrintToKitchen`'in aksine SQL state'i ile ilgilenmez (cart vs
   * existingItems diff yapmaz, `markAsPrinted`'i çağırmaz, ilave sipariş
   * yazdırmaz). Doğrudan verilen `item` + `cancelledQuantity` ile iptal fişi
   * oluşturup mutfak yazıcısına gönderir.
   *
   * Kullanım yeri: `useRestaurantCartOperations.removeFromCartWithRestaurant`
   * (SQL delete ÖNCESİ) ve `updateQuantityWithRestaurant` (SQL update SONRASI).
   *
   * Hata durumunda silme/azaltma akışını bloke etmez (sadece warning loglar).
   */
  const printCancellationForRemovedItem = useCallback(async (params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => {
    const { item, cancelledQuantity, previousQuantity, currentQuantity } = params;

    if (!isRestaurantMode || !companyId) {
      console.warn('[İPTAL FİŞİ] Restoran modu/companyId eksik, iptal fişi atlandı.');
      return;
    }
    if (cancelledQuantity <= 0) {
      console.log('[İPTAL FİŞİ] cancelledQuantity 0 veya negatif, atlandı:', { product: item.name, cancelledQuantity });
      return;
    }

    const selectedQRMenuId = screenSettings?.selectedQRMenuId;
    if (!selectedQRMenuId) {
      console.warn('[İPTAL FİŞİ] QR Menü seçilmemiş — iptal fişi mutfağa gönderilemiyor.');
      return;
    }

    try {
      // Kategori-yazıcı eşlemesi (handlePrintToKitchen ile aynı mantık)
      const printingSettingsParsed = (() => {
        const raw = screenSettings?.PrintingSettingsJson;
        if (!raw) return null;
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
      })();
      const isCategoryMode = printingSettingsParsed?.printingMode === 'category';
      const categoryAssignmentsMap: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments || {};
      // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
      const categoryAssignmentsMap2: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments2 || {};

      // stockVisibility.products → productCategoryLookup (warehouse-bazlı)
      const productCategoryLookup: Record<string, string> = {};
      const allProductSettings = screenSettings?.stockVisibility?.products || {};
      for (const wId of Object.keys(allProductSettings)) {
        const wp = allProductSettings[wId];
        if (wp && typeof wp === 'object') {
          for (const cId of Object.keys(wp)) {
            const pIds = wp[cId];
            if (Array.isArray(pIds)) {
              for (const pid of pIds) {
                if (!productCategoryLookup[pid]) productCategoryLookup[pid] = cId;
              }
            }
          }
        }
      }

      const inv = sqlInventoryItems.find((x: any) => x.id === item.id);
      // 🔑 stockVisibility kategorisi ÖNCELİKLİ — categoryPrinterAssignments
      // bu kategori ID'lerine göre yapılır. Inventory ID son çare.
      const resolvedCategoryId = productCategoryLookup[item.id]
        || (item as any).categoryId
        || inv?.categoryId
        || undefined;

      // Kategori-bazlı modda yazıcı atanmamışsa sessizce skip
      // (kullanıcı kasten o kategoriye yazıcı atamamış olabilir).
      if (isCategoryMode && (!resolvedCategoryId || (!categoryAssignmentsMap[resolvedCategoryId] && !categoryAssignmentsMap2[resolvedCategoryId]))) {
        console.log('[İPTAL FİŞİ] Bu ürünün kategorisinde yazıcı atanmamış, iptal fişi atlandı:', {
          product: item.name,
          categoryId: resolvedCategoryId,
        });
        return;
      }

      const cancelSiparisDetaylari = [{
        UrunId: item.id || '',
        UrunAdi: item.name,
        BirimFiyat: 0,
        Miktar: cancelledQuantity,
        ToplamFiyat: 0,
        UrunResmi: null,
        Aciklama: `İPTAL: ${previousQuantity} → ${currentQuantity}`,
        CategoryId: resolvedCategoryId || null,
        VatRate: null,
      }];

      const cancelKitchenOrderRequest = {
        QRMenuId: selectedQRMenuId,
        PersonelFirebaseUid: quickSaleUserData?.uid || null,
        PersonelName: waiterName || 'Garson',
        ToplamTutar: 0,
        SiparisDetaylari: cancelSiparisDetaylari,
        SiparisNotu: `Masa: ${tableName} (İPTAL)`,
        OdemeYontemi: 'İptal',
        IslemId: null,
        DekontId: null,
        QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id) : null,
        ReferenceNumber: tableName,
        IsRestaurantMode: true,
        IsCancellationOrder: true,
        ReservedLongCode: reservedLongCode || null,
        ReservedShortCode: reservedShortCode || null,
        TableName: tableName || null,
        CustomerAccountName: selectedCustomerName || null,
      };

      console.log('🖨️ [İPTAL FİŞİ] Mutfak yazıcısına gönderiliyor:', {
        product: item.name,
        cancelledQuantity,
        previousQuantity,
        currentQuantity,
        categoryId: resolvedCategoryId,
      });

      const cancelResponse = await salesService.createKitchenOrderFromQuickSale(cancelKitchenOrderRequest);

      if (cancelResponse.success) {
        const cancelReceiptData = {
          orderNumber: reservedShortCode || `RS-${Date.now()}`,
          tableName: tableName || undefined,
          waiterName: waiterName || undefined,
          items: [{
            id: item.id || '',
            name: item.name,
            quantity: cancelledQuantity,
            note: `İptal: ${previousQuantity} → ${currentQuantity}`,
            categoryId: resolvedCategoryId,
          }] as KitchenOrderItem[],
          orderNote: 'İPTAL EDİLEN ÜRÜNLER',
          companyName: screenSettings?.receiptDisplaySettings?.companyName || undefined,
          isCancellationOrder: true,
        };

        await printKitchenReceiptByCategory(cancelReceiptData);
        showSnackbar(`İptal fişi mutfağa gönderildi: ${item.name} (${cancelledQuantity} adet)`, 'warning');
        console.log('✅ [İPTAL FİŞİ] Yazdırıldı');
      } else {
        console.warn('⚠️ [İPTAL FİŞİ] Mutfak ekranına gönderilemedi:', cancelResponse.message);
      }
    } catch (error: any) {
      // Hata durumunda silme/azaltma akışını BLOKE ETME — sadece logla.
      console.warn('⚠️ [İPTAL FİŞİ] İptal fişi gönderilemedi:', error?.message || error);
    }
  }, [isRestaurantMode, companyId, screenSettings, sqlInventoryItems, quickSaleUserData, waiterName, tableName, currentScreen, reservedLongCode, reservedShortCode, selectedCustomerName, salesService, printKitchenReceiptByCategory, showSnackbar]);

  /**
   * "Yine de Devam Et" butonuna tıklandığında
   */
  const handleContinueWithoutPrinting = useCallback(() => {
    console.log('🚶 [GERİ BUTONU] Kullanıcı yazdırmadan devam etmeyi seçti.');
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [navigate, restaurantScreenId]);

  /**
   * Restaurant Modu - Geri Butonu Kontrolü
   * Sepette yazdırılmamış (ilave) ürün varsa uyarı modalı gösterir.
   * NOT: İptal kontrolü kaldırıldı — iptal fişi silme/azaltma anında
   * otomatik basıldığı için geri butonunda iptal uyarısı gösterilmez.
   */
  const handleBackButtonClick = useCallback(async () => {
    if (!isRestaurantMode) {
      navigate(`/restaurant-screen/${restaurantScreenId}`);
      return;
    }

    try {
      console.log('🔙 [GERİ BUTONU] Yazdırılmamış (ilave) ürün kontrolü yapılıyor...', {
        cartLength: cart.length,
        tableId,
        restaurantScreenId
      });

      const { restaurantActiveSaleApi, restaurantKitchenPrintApi } = await import('../../../../services/restaurantApiService');

      // Karar sunucuda: "ne basılmamış" bilgisi önizlemeden gelir (bayat sepet sorunu yok).
      const unprintedItems: any[] = [];
      let hasActiveSale = false;

      if (tableId) {
        const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
        if (activeSale) {
          hasActiveSale = true;
          const preview = await restaurantKitchenPrintApi.preview(activeSale.Id);
          console.log('📊 [GERİ BUTONU] Sunucu önizlemesi:', {
            printCount: preview.PrintCount,
            toPrint: preview.ToPrint.length,
            alreadyPrinted: preview.AlreadyPrinted.length,
          });
          for (const line of preview.ToPrint) {
            const cartRow = cart.find((c: CartItem) => c.sqlItemId === line.ItemId);
            const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === line.ProductId);
            const printed = preview.AlreadyPrinted.find((p) => p.ItemId === line.ItemId)?.PrintedQuantity || 0;
            unprintedItems.push({
              id: line.ProductId,
              name: line.Name,
              price: line.UnitPrice || 0,
              vatRate: cartRow?.vatRate,
              quantity: line.Quantity,
              printedQuantity: printed,
              unprintedQuantity: line.Quantity,
              categoryId: (cartRow as any)?.categoryId || inventoryItem?.categoryId || undefined,
              note: line.Note || cartRow?.note || undefined,
            });
          }
        }
      }

      if (cart.length === 0 && !hasActiveSale) {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
        return;
      }

      // ─────────────────────────────────────────────────────────
      // İPTAL TESPİTİ KALDIRILDI (bilinçli):
      // Ürün silme ve miktar azaltma akışları iptal fişini artık OTOMATİK
      // bastırıyor (useRestaurantCartOperations → printCancellationForRemovedItem)
      // ve PrintedQuantity'yi senkron tutuyor / SQL satırını siliyor.
      // Bu yüzden geri butonunda "yazdırılmayan iptal ürünler var" uyarısına
      // gerek kalmadı; modal iptal nedeniyle HİÇBİR ZAMAN açılmamalı.
      // İlave (hiç yazdırılmamış yeni ürün) uyarısı ise korunuyor.
      // ─────────────────────────────────────────────────────────

      // ─────────────────────────────────────────────────────────
      // KATEGORI-BAZLI YAZICISIZ URUN FILTRESI
      //
      // Eger ekran kategori-bazli yazdirma modunda ise, kategorisinde
      // yazici atanmamis urunler "yazdirilmamis" olarak SAYILMAMALI:
      // - Restoran isletmesi bazi urunlerin (orn. icecek, tatli) mutfaga
      //   yazdirilmasini istemez ve kasten o kategorilere yazici atamaz.
      // - Bu urunler eklendiginde kullanici geri dondukten sonra "yazdirilmamis
      //   urun var" uyarisini almamali (sessiz skip).
      //
      // Tek-yazici modunda filtre ETKISIZ (her sey ayni yaziciya gider).
      // ─────────────────────────────────────────────────────────
      const printingSettingsParsed = (() => {
        const raw = (screenSettings as any)?.PrintingSettingsJson;
        if (!raw) return null;
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
      })();
      const isCategoryMode = printingSettingsParsed?.printingMode === 'category';
      const categoryAssignmentsMap: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments || {};
      // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
      const categoryAssignmentsMap2: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments2 || {};

      // productCategoryLookup — handlePrintToKitchen'daki ile birebir ayni
      // (warehouse-bazli stockVisibility.products yapisi).
      const productCategoryLookup: Record<string, string> = {};
      const allProductSettings = screenSettings?.stockVisibility?.products || {};
      for (const wId of Object.keys(allProductSettings)) {
        const wp = allProductSettings[wId];
        if (wp && typeof wp === 'object') {
          for (const cId of Object.keys(wp)) {
            const pIds = wp[cId];
            if (Array.isArray(pIds)) {
              for (const pid of pIds) {
                if (!productCategoryLookup[pid]) productCategoryLookup[pid] = cId;
              }
            }
          }
        }
      }

      // 🔑 ÖNEMLİ: categoryPrinterAssignments, hızlı satış ekran ayarlarındaki
      // depo-stok tab'ında seçilen kategorilere (stockVisibility) göre atanır.
      // Bu yüzden ürünün Inventory tablosundaki orijinal kategorisi YANLIŞ
      // sonuç verebilir (ID'ler örtüşmeyebilir). Önce stockVisibility lookup,
      // SONRA Inventory fallback (son çare) kullanılır.
      const hasPrinterForProduct = (productId: string, fallbackCategoryId?: string): boolean => {
        if (!isCategoryMode) return true;
        const inv = sqlInventoryItems.find((x: any) => x.id === productId);
        const catId = productCategoryLookup[productId] || fallbackCategoryId || inv?.categoryId;
        if (!catId) return false;
        return !!categoryAssignmentsMap[catId] || !!categoryAssignmentsMap2[catId];
      };

      const filteredUnprinted = unprintedItems.filter((u: any) =>
        hasPrinterForProduct(u.id, u.categoryId)
      );

      const skippedCount = unprintedItems.length - filteredUnprinted.length;
      if (skippedCount > 0) {
        console.log(
          `[GERİ BUTONU] ${skippedCount} kalem yazicisiz kategoriye ait — filtrelendi (uyarida gorunmeyecek)`
        );
      }

      if (filteredUnprinted.length > 0) {
        console.log(`⚠️ [GERİ BUTONU] UYARI: ${filteredUnprinted.length} kalem ilave (yazdırılmamış) var!`);
        setUnprintedItemsList(filteredUnprinted);
        setCancelledItemsList([]);
        setUnprintedItemsWarningOpen(true);
        return;
      }

      console.log('✅ [GERİ BUTONU] Tüm ürünler yazdırılmış (veya yazicisiz kategoride), geri dönülüyor... (iptal kontrolü kaldırıldı — otomatik iptal fişi basılıyor)');
      navigate(`/restaurant-screen/${restaurantScreenId}`);

    } catch (error: any) {
      console.error('❌ [GERİ BUTONU] Kontrol hatası:', error);
      if (cart.length > 0) {
        const allUnprinted = cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price || 0,
          vatRate: item.vatRate,
          quantity: item.quantity,
          printedQuantity: 0,
          unprintedQuantity: item.quantity,
        }));
        setUnprintedItemsList(allUnprinted);
        setCancelledItemsList([]);
        setUnprintedItemsWarningOpen(true);
        return;
      }
      navigate(`/restaurant-screen/${restaurantScreenId}`);
    }
  }, [isRestaurantMode, cart, tableId, restaurantScreenId, navigate, setUnprintedItemsList, setCancelledItemsList, setUnprintedItemsWarningOpen, sqlInventoryItems, screenSettings]);

  /**
   * RESTAURANT MODU: Mutfağa Yazdır — KARAR SUNUCUDA.
   *
   * Eski akış farkı (adet − yazdırılan) bu cihazın sepetinden hesaplıyordu; aynı masa
   * başka bir cihazda açıkken sepet bayat kalıyor, başka cihazın eklediği satır
   * "silindi" sanılıp iptal fişi basılıyor ve yazdırıldı adedi sıfırlanıyordu
   * (ürün ikinci kez mutfağa gidiyordu). Artık:
   *   1) preview  → sunucu neyi basacağını söyler (değişiklik yok)
   *   2) kategori yazıcı kontrolü (ekran ayarı; istemcide)
   *   3) POST kitchen-print → sunucu masa satırını kilitler, farkı hesaplar,
   *      "yazdırıldı" işaretler, fiş + mutfak ekranı kaydını yazar, listeyi döner
   *   4) dönen liste yerel yazıcıdan basılır
   * İki cihaz aynı anda bassa ikincisi boş liste alır. Aynı RequestId tekrar
   * gönderilirse sunucu saklı cevabı döner (ağ tekrarında ikinci fiş yok).
   *
   * `options.skipCountdown=true`: başarı sonrası masa-dönüş geri sayımı tetiklenmez.
   */
  const handlePrintToKitchen = useCallback(async (
    options?: { skipCountdown?: boolean },
  ) => {
    if (!isRestaurantMode) {
      showSnackbar('Bu fonksiyon sadece restoran modunda kullanılabilir.', 'warning');
      return;
    }

    if (!tableId) {
      showSnackbar('Masa bilgisi bulunamadı.', 'error');
      return;
    }

    // Aynı sekmede çift tıklama kilidi; cihazlar arası koruma sunucuda.
    if (kitchenPrintInFlightRef.current) {
      console.warn('⛔ [MUTFAĞA YAZDIR] Yazdırma zaten devam ediyor — mükerrer tetikleme engellendi.');
      showSnackbar('Yazdırma devam ediyor, lütfen bekleyin...', 'warning');
      return;
    }
    kitchenPrintInFlightRef.current = true;

    try {
      const selectedQRMenuId = screenSettings?.selectedQRMenuId;
      if (!selectedQRMenuId) {
        showSnackbar('Mutfak ekranı için QR Menü seçilmemiş! Ayarlardan QR Menü seçiniz.', 'error');
        return;
      }
      if (!companyId) {
        showSnackbar('Şirket ID bulunamadı!', 'error');
        return;
      }

      const { restaurantActiveSaleApi, restaurantKitchenPrintApi } = await import('../../../../services/restaurantApiService');

      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      if (!activeSale) {
        if (cart.length === 0) {
          showSnackbar('Sepette ürün yok.', 'warning');
        } else {
          showSnackbar('Masa satış kaydı bulunamadı. Masadan çıkıp tekrar girin.', 'error');
        }
        return;
      }

      // 1) Önizleme — sunucu "ne basılır" der
      const preview = await restaurantKitchenPrintApi.preview(activeSale.Id);
      if (preview.ToPrint.length === 0) {
        showSnackbar('Tüm ürünler zaten mutfağa yazdırılmış!', 'info');
        syncPrintedFromServer(preview.AlreadyPrinted);
        return;
      }

      // 2) Kategori yazıcı kontrolü
      const printer = buildPrinterResolver();
      const skippedPreview = printer.isCategoryMode
        ? preview.ToPrint.filter((l) => !printer.hasPrinter(l.ProductId, (cart.find((c: CartItem) => c.sqlItemId === l.ItemId) as any)?.categoryId))
        : [];
      const skippedItemsPreview = skippedPreview.map((l) => {
        const catId = printer.categoryIdOf(l.ProductId);
        return { name: l.Name, quantity: l.Quantity, categoryName: printer.categoryNameOf(catId), categoryId: catId };
      });

      if (printer.isCategoryMode && skippedPreview.length === preview.ToPrint.length) {
        // Hiçbir ürünün kategorisinde yazıcı yok → sunucuya gitme, uyar
        const cats = Array.from(new Set(skippedItemsPreview.map((i) => i.categoryName || '(Kategorisi olmayan)')));
        console.warn('🚫 [MUTFAĞA YAZDIR] Tüm ürünlerin kategorisinde yazıcı atanmamış — yazdırma iptal:', { cats, skippedItemsPreview });
        if (showNoCategoryPrinterModal) {
          showNoCategoryPrinterModal(cats, skippedItemsPreview);
        } else {
          showSnackbar(`Bu kategori${cats.length > 1 ? 'lere' : 'ye'} yazıcı atanmamış: ${cats.join(', ')}`, 'warning');
        }
        return;
      }

      // 3) Bilgi modalı: yazdırılmış + yazdırılacak + (varsa) atlanan
      if (showKitchenPrintInfoModal) {
        const skippedIds = new Set(skippedPreview.map((l) => l.ItemId));
        showKitchenPrintInfoModal(
          preview.AlreadyPrinted.map((p) => ({ name: p.Name, quantity: p.PrintedQuantity })),
          preview.ToPrint.filter((l) => !skippedIds.has(l.ItemId)).map((l) => ({ name: l.Name, quantity: l.Quantity })),
          skippedItemsPreview.length > 0 ? skippedItemsPreview : undefined,
        );
      }

      // 4) Sunucu kararı (kilitli): işaretler, fiş + mutfak ekranı kaydını yazar
      const ticket = await restaurantKitchenPrintApi.print(activeSale.Id, {
        requestId: newRequestId(),
        actorName: waiterName || 'Garson',
        actorUid: quickSaleUserData?.uid || null,
        qrMenuId: selectedQRMenuId,
        quickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id) : null,
        customerAccountName: selectedCustomerName || null,
      });

      if (ticket.NothingToPrint) {
        // Önizleme ile karar arasında başka bir cihaz yazdırdı
        showSnackbar('Ürünler az önce başka bir cihazdan mutfağa yazdırıldı; tekrar basılmadı.', 'info');
        syncPrintedFromServer(ticket.AlreadyPrinted);
        return;
      }

      console.log(`🖨️ [MUTFAĞA YAZDIR] Sunucu kararı: fiş #${ticket.PrintNo}, ${ticket.Items.length} satır`, ticket.Items.map((i) => `${i.Name} x${i.Quantity}`));

      // 5) Fişi bas — yalnız yazıcısı olan satırlar
      const printable = ticket.Items.filter((l) => printer.hasPrinter(l.ProductId, (cart.find((c: CartItem) => c.sqlItemId === l.ItemId) as any)?.categoryId));
      if (printable.length > 0) {
        try {
          await printKitchenReceiptByCategory(buildKitchenReceipt(ticket, printable));
        } catch (printError: any) {
          console.warn('⚠️ [MUTFAĞA YAZDIR] Mutfak fişi yazdırılamadı:', printError?.message);
          showSnackbar('Sipariş mutfak ekranına gitti ama yazıcıdan çıkmadı. Yazıcıyı kontrol edin ve mutfağa haber verin.', 'warning');
        }
      }

      // 6) Yerel sepet: basılan satırların tamamı yazdırıldı
      if (setCart) {
        const printedIds = new Set(ticket.Items.map((l) => l.ItemId));
        const printedBefore = new Map(ticket.AlreadyPrinted.map((p) => [p.ItemId, p.PrintedQuantity]));
        setCart((prevCart) => prevCart.map((cartItem) => {
          if (!cartItem.sqlItemId) return cartItem;
          if (printedIds.has(cartItem.sqlItemId)) return { ...cartItem, printedQuantity: cartItem.quantity };
          const before = printedBefore.get(cartItem.sqlItemId);
          return before !== undefined ? { ...cartItem, printedQuantity: Math.min(before, cartItem.quantity) } : cartItem;
        }));
      }

      const message = ticket.IsAdditionalOrder
        ? `${tableName} - İlave sipariş mutfağa gönderildi! (${ticket.Items.length} kalem)`
        : `${tableName} mutfağa gönderildi! (${ticket.Items.length} kalem)`;
      showSnackbar(message, 'success');
      if (!options?.skipCountdown) {
        onAfterSuccessfulKitchenPrint?.();
      }
    } catch (error: any) {
      console.error('❌ [MUTFAĞA YAZDIR] Hata:', error);
      const serverMessage = error?.response?.data?.Message || error?.response?.data?.message;
      showSnackbar(`Mutfağa yazdırılamadı: ${serverMessage || error?.message || 'bilinmeyen hata'}`, 'error');
    } finally {
      kitchenPrintInFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestaurantMode, cart, tableId, tableName, restaurantScreenId, companyId, waiterName, screenSettings, currentScreen, quickSaleUserData, reservedShortCode, sqlInventoryItems, allCategoriesData, printKitchenReceiptByCategory, showSnackbar, setCart, onAfterSuccessfulKitchenPrint, selectedCustomerName, showKitchenPrintInfoModal, showNoCategoryPrinterModal]);

  /** Sunucudaki "yazdırıldı" adetlerini yerel sepete yansıt (bayat sepet düzeltmesi). */
  function syncPrintedFromServer(printed: { ItemId: number; PrintedQuantity: number }[]) {
    if (!setCart) return;
    const map = new Map(printed.map((p) => [p.ItemId, p.PrintedQuantity]));
    setCart((prevCart) => prevCart.map((cartItem) => {
      if (!cartItem.sqlItemId || !map.has(cartItem.sqlItemId)) return cartItem;
      return { ...cartItem, printedQuantity: Math.min(map.get(cartItem.sqlItemId)!, cartItem.quantity) };
    }));
  }


  // ─────────────────────────────────────────────────────────
  // GERI-DONUS MODAL CALLBACK'LERI — DELEGASYON
  //
  // `UnprintedItemsWarningModal`'in uc butonu da (Mutfaga Yazdir / Iptal /
  // Her Ikisi) ana `handlePrintToKitchen` fonksiyonunu cagirir. Ana fonksiyon
  // ZATEN su islemleri YAPAR:
  //   - Cart vs SQL diff ile ilave + iptal kalemlerini birlikte tespit
  //   - Kategori-bazli yazdirma + pre-compute skip kontrolu
  //   - "Kategoride yazici yok" uyari modali (gerekirse)
  //   - Yazdirma bilgi modali (yazdirilan/yazdirilacak/atlanan)
  //   - menuNormalItems + selectedOptions (modifier) ile zengin subItems
  //   - productCategoryLookup (warehouse-bazli) kategori fallback chain
  //   - Mutfak ekranina createKitchenOrderFromQuickSale + markAsPrinted
  //   - Iptal fisi mutfaga + SQL PrintedQuantity guncelle
  //   - Local cart printedQuantity guncelle + snackbar bildir
  //
  // Gecmisteki kopya-yapistir 3 fonksiyon (~500 satir) bu zenginligi
  // kacirdigi icin user "yazdirmiyor" diyordu. Delegasyon ile parite:
  //   - drift onlenir, tek dogruluk kaynagi
  //   - kullanicinin masa-donus geri sayim modali ({ skipCountdown: true })
  //     bu akista flash etmez; navigate hemen olur
  //
  // NOT: Modal'in 3 farkli butonu (Sadece Ilave / Sadece Iptal / Her Ikisi)
  // ayni davranisi tetikler — ana fonksiyon zaten ilave + iptal kalemlerini
  // ayni cagride birlikte isler. Bu kullanici tarafindan onaylandi (mutfak
  // iptalden de haberdar olmali).
  // ─────────────────────────────────────────────────────────

  /**
   * Mutfaga Yazdir ve Geri Don — Modal'dan cagrilir (Ilave urunler icin)
   */
  const handlePrintToKitchenAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * Iptal Fisi Yazdir ve Geri Don — Modal'dan cagrilir (Iptal urunler icin)
   *
   * Ana fonksiyon iptal kalemlerini de zaten isler.
   */
  const handlePrintCancellationAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (iptal, skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * Hem Ilave hem Iptal Yazdir ve Geri Don — Modal'dan cagrilir
   */
  const handlePrintBothAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (her ikisi, skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * RESTAURANT MODU - Adisyon Yazdır (Müşteri Fişi)
   */
  const handlePrintCheck = useCallback(async () => {
    if (!isRestaurantMode) {
      showSnackbar('Bu fonksiyon sadece restoran modunda kullanılabilir.', 'warning');
      return;
    }

    if (cart.length === 0) {
      showSnackbar('Sepette ürün yok.', 'warning');
      return;
    }

    if (!tableId) {
      showSnackbar('Masa bilgisi bulunamadı.', 'error');
      return;
    }

    try {
      console.log('🧾 [ADİSYON YAZDIR] Adisyon yazdırılıyor...', {
        restaurantScreenId,
        tableId,
        tableName,
        cartItems: cart.length,
        total: calculateTotal(),
        lastAppliedDiscountAmount,
        appliedDiscountType,
        discountInputPercentage,
        showDiscountInfo: screenSettings?.receiptDisplaySettings?.showDiscountInfo,
      });

      const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
      const { printAdisyonReceipt } = await import('../components/AdisyonPrintService');

      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      if (!activeSale) {
        showSnackbar('Bu masa için aktif satış bulunamadı.', 'error');
        return;
      }

      const receiptItems = cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.price,
        totalPrice: item.price * item.quantity,
        vatRate: item.vatRate || 18,
      }));

      const grandTotal = calculateTotal();

      // ✅ Fiş ayarlarını al (logo dahil)
      const receiptDisplaySettings = screenSettings?.receiptDisplaySettings;
      
      const discountAmt = lastAppliedDiscountAmount || 0;
      const discountPct = appliedDiscountType === 'percentage' && discountInputPercentage ? parseFloat(discountInputPercentage) : undefined;

      console.log('🏷️ [ADİSYON İNDİRİM] discountAmt:', discountAmt, '| showDiscountInfo ayarı:', receiptDisplaySettings?.showDiscountInfo, '| appliedDiscountType:', appliedDiscountType, '| discountPct:', discountPct, '| grandTotal:', grandTotal);

      // ✅ YENİ: "Adisyonda İlk Açan Garson" toggle'ı açıkken receiptWaiterName dolu gelir.
      // Diğer akışlar (mutfak yazdır, iptal vb.) bu değerden ETKİLENMEZ.
      const effectiveReceiptWaiterName = receiptWaiterName || waiterName || undefined;
      console.log('🧾 [ADİSYON YAZDIR] Garson adı seçimi:', {
        receiptWaiterName,
        loggedInWaiterName: waiterName,
        effectiveReceiptWaiterName,
      });

      const adisyonData = {
        invoiceNumber: `ADİSYON-${activeSale.ReservedShortCode || activeSale.Id}`,
        date: new Date().toLocaleDateString('tr-TR'),
        time: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
        tableName: tableName || 'Masa',
        items: receiptItems,
        subtotal: grandTotal + discountAmt,
        vatTotal: 0,
        grandTotal: grandTotal,
        waiterName: effectiveReceiptWaiterName,
        orderNumber: activeSale.ReservedShortCode || undefined,
        companyInfo: {
          name: receiptDisplaySettings?.companyName || '',
          address: receiptDisplaySettings?.companyAddress || '',
          taxNumber: receiptDisplaySettings?.taxNumber || '',
        },
        standardFooterNote: receiptDisplaySettings?.standardFooterNote || '',
        thankYouMessage: receiptDisplaySettings?.thankYouMessage || 'Bizi tercih ettiğiniz için teşekkür ederiz!',
        headerType: receiptDisplaySettings?.headerType || 'text',
        logoUrl: receiptDisplaySettings?.logoUrl || '',
        showDiscountInfo: !!receiptDisplaySettings?.showDiscountInfo && discountAmt > 0,
        discountAmount: discountAmt,
        discountTotal: (!!receiptDisplaySettings?.showDiscountInfo && discountAmt > 0) ? discountAmt : 0,
        discountType: appliedDiscountType || null,
        discountPercentage: discountPct,
      };

      try {
        // ✅ YENİ: Yazdırma moduna göre yazıcı seç
        // - Tek yazıcı modu: defaultReceiptPrinter kullan
        // - Kategori bazlı mod: adisyonPrinter kullan (restoran için özel)
        const printingMode = (screenSettings as any)?.printingMode || 'category';
        const defaultPrinter = (screenSettings as any)?.defaultReceiptPrinter;
        const adisyonPrinter = (screenSettings as any)?.adisyonPrinter;
        
        // Kullanılacak yazıcıyı belirle
        let printerToUse = null;
        if (printingMode === 'single' && defaultPrinter) {
          printerToUse = defaultPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Tek yazıcı modu - Varsayılan yazıcı kullanılacak:', defaultPrinter.name);
        } else if (printingMode === 'category' && adisyonPrinter) {
          printerToUse = adisyonPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Kategori modu - Adisyon yazıcısı kullanılacak:', adisyonPrinter.name);
        } else if (defaultPrinter) {
          // Fallback: Adisyon yazıcısı yoksa varsayılan yazıcıyı dene
          printerToUse = defaultPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Fallback - Varsayılan yazıcı kullanılacak:', defaultPrinter.name);
        }
        
        if (printerToUse) {
          // ✅ localServiceUrl'u screenSettings'den al
          const localServiceUrl = (screenSettings as any)?.localServiceUrl || 'http://localhost:3003';
          await printAdisyonReceipt(adisyonData, printerToUse, localServiceUrl);
          console.log('✅ [ADİSYON YAZDIR] Fiş yazıcıya gönderildi:', printerToUse.name);
        } else {
          console.warn('⚠️ [ADİSYON YAZDIR] Yazıcı tanımlı değil. Yazdırma modu:', printingMode, 
            '| Varsayılan:', defaultPrinter?.name || 'YOK', 
            '| Adisyon:', adisyonPrinter?.name || 'YOK');
        }
      } catch (printError) {
        console.warn('⚠️ [ADİSYON YAZDIR] Fiş yazdırma hatası:', printError);
      }

      const markResult = await restaurantActiveSaleApi.markReceiptPrinted(
        activeSale.Id,
        waiterName || 'Bilinmeyen'
      );

      if (markResult.Success) {
        showSnackbar(`${tableName} adisyonu yazdırıldı! (₺${grandTotal.toFixed(2)})`, 'success');
        setIsReceiptPrintedForCurrentTable(true);
        // ✅ YENİ: Adisyon yazdırma başarılı → masalara dönüş geri sayımını tetikle
        // (mutfak yazdırma akışıyla aynı pattern: kullanıcı geri sayım modalında
        // iptal de edebilir, beklerse otomatik RestaurantScreen'e döner).
        onAfterSuccessfulCheckPrint?.();
      } else {
        showSnackbar(`Adisyon yazdırıldı ama kayıt yapılamadı: ${markResult.Message}`, 'warning');
      }

    } catch (error: any) {
      console.error('❌ [ADİSYON YAZDIR] Hata:', error);
      showSnackbar(`Hata oluştu: ${(error as Error).message}`, 'error');
    }
  }, [isRestaurantMode, cart, tableId, tableName, restaurantScreenId, waiterName, receiptWaiterName, calculateTotal, screenSettings, showSnackbar, setIsReceiptPrintedForCurrentTable, lastAppliedDiscountAmount, appliedDiscountType, discountInputPercentage, onAfterSuccessfulCheckPrint]);

  return {
    handlePrintToKitchen,
    handlePrintCheck,
    handleBackButtonClick,
    handleContinueWithoutPrinting,
    handlePrintToKitchenAndGoBack,
    handlePrintCancellationAndGoBack,
    handlePrintBothAndGoBack,
    handleCloseTable,
    printCancellationForRemovedItem,
  };
};

export default useRestaurantKitchenOperations;
